/**
 * Configuración → Métodos de pago (`/configuracion/metodos-pago`).
 *
 * Puerto de `Metodo_pago` + `views/metodo_pago/index.php`: el catálogo que usan
 * las ventas y los movimientos de cuenta, con los datos que pide el programa de
 * fidelidad (`bonus_code`, `bonus_name`) y el código contable.
 *
 * Reglas conservadas: `nombre` y `tipo` obligatorios, `bonus_code` numérico si se
 * informa (queda nulo si está vacío), `bonus_name` y `original_type` caen al
 * nombre, `codigo_contable` de 20 caracteres como máximo y la IP/POS vacías se
 * guardan nulas.
 *
 * Diferencias anotadas: el nombre no puede repetirse (409) y **editar no
 * reactiva** el método (el panel anterior forzaba `estado = 1` en cada guardado,
 * así que editar un método desactivado lo volvía a activar sin avisar).
 */

import { ArrowLeft, Pencil, Plus, Power, ShieldAlert, Wallet } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';

import { useMetodosPago, useMutarMetodosPago } from '../catalogos.api';
import type { MetodoPago, MetodoPagoPayload } from '../catalogos.types';

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

const FORMULARIO_VACIO: MetodoPagoPayload = {
  nombre: '',
  tipo: '',
  bonus_code: '',
  bonus_name: '',
  original_type: '',
  codigo_contable: '',
  ip_address: '',
  pos_identifier: '',
};

/** Alta/edición de un método de pago. */
function MetodoDialogo({
  metodo,
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  metodo: MetodoPago | null;
  guardando: boolean;
  error: string | null;
  onGuardar: (payload: MetodoPagoPayload, activo?: boolean) => void;
  onCerrar: () => void;
}) {
  const [datos, setDatos] = useState<MetodoPagoPayload>(
    metodo
      ? {
          nombre: metodo.nombre,
          tipo: metodo.tipo,
          bonus_code: metodo.bonus_code === null ? '' : String(metodo.bonus_code),
          bonus_name: metodo.bonus_name,
          original_type: metodo.original_type,
          codigo_contable: metodo.codigo_contable,
          ip_address: metodo.ip_address,
          pos_identifier: metodo.pos_identifier,
        }
      : FORMULARIO_VACIO,
  );
  const [activo, setActivo] = useState(metodo?.activo ?? true);

  const cambiar = <K extends keyof MetodoPagoPayload,>(campo: K, valor: MetodoPagoPayload[K]) =>
    setDatos((previo) => ({ ...previo, [campo]: valor }));

  const valido = datos.nombre.trim().length > 0 && datos.tipo.trim().length > 0;

  return (
    <Dialog
      open
      onClose={onCerrar}
      size="lg"
      title={metodo ? `Editar ${metodo.nombre}` : 'Nuevo método de pago'}
      description="Se usa en las ventas, en los movimientos de cuenta y en el programa de fidelidad."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!valido}
            onClick={() =>
              onGuardar(
                {
                  ...datos,
                  nombre: datos.nombre.trim(),
                  tipo: datos.tipo.trim(),
                  bonus_name: datos.bonus_name.trim(),
                  original_type: datos.original_type.trim(),
                  codigo_contable: datos.codigo_contable.trim(),
                  ip_address: datos.ip_address.trim(),
                  pos_identifier: datos.pos_identifier.trim(),
                },
                metodo ? activo : undefined,
              )
            }
          >
            Guardar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? <Alert variant="error" title="No se pudo guardar">{error}</Alert> : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Nombre"
            value={datos.nombre}
            maxLength={200}
            autoFocus
            placeholder="Ej. CONTADO"
            onChange={(evento) => cambiar('nombre', evento.target.value)}
          />
          <Input
            label="Tipo"
            value={datos.tipo}
            maxLength={200}
            hint="Valores usados en esta instalación: CASH, CUENTA"
            placeholder="Ej. CASH"
            className="uppercase"
            onChange={(evento) => cambiar('tipo', evento.target.value.toUpperCase())}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Bonus Code"
            type="number"
            value={datos.bonus_code}
            hint="Opcional · sólo números"
            placeholder="Ej. 1"
            onChange={(evento) => cambiar('bonus_code', evento.target.value)}
          />
          <Input
            label="Bonus Name"
            value={datos.bonus_name}
            maxLength={20}
            hint="Si se deja vacío se usa el nombre"
            placeholder="Ej. Tarjeta de crédito"
            onChange={(evento) => cambiar('bonus_name', evento.target.value)}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Original Type"
            value={datos.original_type}
            maxLength={50}
            hint="Si se deja vacío se usa el nombre"
            placeholder="Ej. CONTADO"
            onChange={(evento) => cambiar('original_type', evento.target.value)}
          />
          <Input
            label="Código contable"
            value={datos.codigo_contable}
            maxLength={20}
            placeholder="Ej. 121201"
            onChange={(evento) => cambiar('codigo_contable', evento.target.value)}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Dirección IP"
            value={datos.ip_address}
            maxLength={255}
            hint="Opcional · para terminales con PIN pad"
            placeholder="Ej. 192.168.1.10"
            onChange={(evento) => cambiar('ip_address', evento.target.value)}
          />
          <Input
            label="Identificador del POS"
            value={datos.pos_identifier}
            maxLength={255}
            hint="Opcional"
            placeholder="Ej. POS-001"
            onChange={(evento) => cambiar('pos_identifier', evento.target.value)}
          />
        </div>

        {metodo ? (
          <Checkbox
            label="Método activo"
            hint="Un método inactivo no se ofrece en el POS"
            checked={activo}
            onChange={(evento) => setActivo(evento.target.checked)}
          />
        ) : null}
      </div>
    </Dialog>
  );
}

export function MetodosPagoPage() {
  useDocumentTitle('Configuración · Métodos de pago');

  const metodos = useMetodosPago();
  const mutaciones = useMutarMetodosPago();

  const [dialogo, setDialogo] = useState<{ abierto: boolean; metodo: MetodoPago | null }>({
    abierto: false,
    metodo: null,
  });
  const [aviso, setAviso] = useState<{ tono: 'success' | 'error'; texto: string } | null>(null);
  const [errorDialogo, setErrorDialogo] = useState<string | null>(null);

  const sinPermiso = metodos.error instanceof ApiError && metodos.error.status === 403;
  const lista = metodos.data?.metodos ?? [];

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/configuracion"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Configuración
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Wallet className="size-6 text-brand-500" aria-hidden="true" />
          Métodos de pago
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Formas de pago de las ventas: efectivo, depósitos, tarjetas y billeteras, con su código
          contable y su equivalencia en el programa de fidelidad.
        </p>
      </div>

      <Button
        size="sm"
        leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
        disabled={sinPermiso}
        onClick={() => {
          setErrorDialogo(null);
          setDialogo({ abierto: true, metodo: null });
        }}
      >
        Nuevo método
      </Button>
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>Configuración</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  const guardar = (payload: MetodoPagoPayload, activo?: boolean) => {
    setErrorDialogo(null);
    const alGuardar = {
      onSuccess: (datos: { mensaje: string }) => {
        setDialogo({ abierto: false, metodo: null });
        setAviso({ tono: 'success' as const, texto: datos.mensaje });
      },
      onError: (error: unknown) =>
        setErrorDialogo(mensajeDe(error, 'No se pudo guardar el método de pago.')),
    };

    if (dialogo.metodo) {
      mutaciones.actualizar.mutate(
        { id: dialogo.metodo.id, payload: { ...payload, ...(activo === undefined ? {} : { activo }) } },
        alGuardar,
      );
      return;
    }

    mutaciones.crear.mutate(payload, alGuardar);
  };

  const cambiarEstado = (metodo: MetodoPago) => {
    mutaciones.cambiarEstado.mutate(
      { id: metodo.id, activo: !metodo.activo },
      {
        onSuccess: (datos) => setAviso({ tono: 'success', texto: datos.mensaje }),
        onError: (error) =>
          setAviso({ tono: 'error', texto: mensajeDe(error, 'No se pudo cambiar el estado.') }),
      },
    );
  };

  return (
    <div className="space-y-6">
      {cabecera}

      {aviso ? (
        <Alert variant={aviso.tono === 'success' ? 'success' : 'error'}>{aviso.texto}</Alert>
      ) : null}

      {metodos.isLoading ? (
        <Skeleton className="h-80 w-full" />
      ) : metodos.error && !sinPermiso ? (
        <Alert variant="error" title="No se pudieron leer los métodos de pago">
          {mensajeDe(metodos.error, 'La API no respondió.')}
        </Alert>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Listado de métodos de pago</CardTitle>
            <Badge variant="neutral">{metodos.data?.total ?? 0} registrados</Badge>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[62rem] border-collapse text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                    <th className="pb-2">Nombre</th>
                    <th className="w-24 pb-2">Tipo</th>
                    <th className="w-40 pb-2">Bonus</th>
                    <th className="w-40 pb-2">Original Type</th>
                    <th className="w-28 pb-2">C. contable</th>
                    <th className="w-40 pb-2">Red (IP / POS)</th>
                    <th className="w-24 pb-2 text-center">Movs.</th>
                    <th className="w-24 pb-2">Estado</th>
                    <th className="w-28 pb-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((metodo) => (
                    <tr key={metodo.id} className="border-t border-ink-100">
                      <td className="py-3 font-medium text-ink-900">{metodo.nombre}</td>
                      <td className="py-3">
                        <Badge variant="outline">{metodo.tipo}</Badge>
                      </td>
                      <td className="py-3 text-ink-600">
                        {metodo.bonus_code === null ? (
                          '—'
                        ) : (
                          <>
                            <span className="font-mono text-xs text-ink-400">
                              {metodo.bonus_code}
                            </span>{' '}
                            {metodo.bonus_name}
                          </>
                        )}
                      </td>
                      <td className="py-3 text-ink-600">{metodo.original_type || '—'}</td>
                      <td className="py-3 font-mono text-xs text-ink-600">
                        {metodo.codigo_contable || '—'}
                      </td>
                      <td className="py-3 text-xs text-ink-500">
                        {metodo.ip_address || metodo.pos_identifier ? (
                          <>
                            {metodo.ip_address || '—'}
                            {metodo.pos_identifier ? ` · ${metodo.pos_identifier}` : ''}
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="py-3 text-center text-ink-600">{metodo.movimientos}</td>
                      <td className="py-3">
                        <Badge variant={metodo.activo ? 'success' : 'neutral'}>
                          {metodo.activo ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Editar ${metodo.nombre}`}
                            title="Editar"
                            onClick={() => {
                              setErrorDialogo(null);
                              setDialogo({ abierto: true, metodo });
                            }}
                          >
                            <Pencil className="size-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={
                              metodo.activo ? `Desactivar ${metodo.nombre}` : `Activar ${metodo.nombre}`
                            }
                            title={metodo.activo ? 'Desactivar' : 'Activar'}
                            loading={mutaciones.cambiarEstado.isPending}
                            onClick={() => cambiarEstado(metodo)}
                          >
                            <Power
                              className={`size-4 ${metodo.activo ? 'text-emerald-600' : 'text-ink-400'}`}
                              aria-hidden="true"
                            />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Alert variant="info" className="mt-4">
              Los métodos de pago no se eliminan (los usan las ventas y los movimientos de cuenta):
              se desactivan. La columna <strong>Movs.</strong> indica cuántos movimientos los usan.
            </Alert>
          </CardContent>
        </Card>
      )}

      {dialogo.abierto ? (
        <MetodoDialogo
          key={`metodo-${dialogo.metodo?.id ?? 'nuevo'}`}
          metodo={dialogo.metodo}
          guardando={mutaciones.crear.isPending || mutaciones.actualizar.isPending}
          error={errorDialogo}
          onGuardar={guardar}
          onCerrar={() => setDialogo({ abierto: false, metodo: null })}
        />
      ) : null}
    </div>
  );
}
